import { concat, fn } from '@ember/helper';
/* eslint-disable ember/no-side-effects */
/* eslint-disable ember/no-runloop */
import { action } from '@ember/object';
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismLoadingBar, PrismTooltip } from '@mylighthouse/prism-ember';
import {
  PRISM_SPACING_100,
  PRISM_SPACING_400,
} from '@mylighthouse/prism-foundation';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { and, not, or } from 'ember-truth-helpers';
import GeneralChartsLegendsLegendItem from 'lumen/components/general/charts/legends/legend-item';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import GeneralEmptyStateOtbBanner from 'lumen/components/general/empty-state-otb-banner';
import HotelOverviewDashboardTooltipsTooltipRatesChart from 'lumen/components/hotel/overview-dashboard/tooltips/tooltip-rates-chart';
import { getWeekendPlotBands } from 'lumen/utils/charts/chart-utils';
import {
  calculateTickPositions,
  calculateYaxisMax,
  connectRateDataPoint,
  connectUndefinedRateDataPoint,
  getSeriesConfiguration,
  xAxistickPositionerForOverviewDashboard,
} from 'lumen/utils/charts/rates-chart-utils';
import {
  DAY_IN_SECONDS,
  HIGHCHARTS_MISSING_MARKER,
} from 'lumen/utils/constants/general-constants';
import {
  OTB_SS_ENTRY_POINTS,
  OTB_SS_VIA_QP_VALUES,
} from 'lumen/utils/constants/otb-selfservice-constants';
import {
  OD_API_DATA_KEYS,
  OD_SELECTED_TILES_IDS,
} from 'lumen/utils/constants/overview-dashboard-constants';
import {
  SHORT_TERM_RENTAL,
  SHORT_TERM_RENTAL_RATE,
} from 'lumen/utils/constants/short-term-rental-constants';

interface HotelOverviewDashboardTilesTopInsightsRatesChartSignature {
  Args: {
    isLoading: unknown;
    isPerformanceChartVisible: unknown;
    onUpdateChartReference: unknown;
    priceDifferenceData: unknown;
    priceOpportunityData: unknown;
    selectedRefDaysAgo: unknown;
    toggleOrSetSelectedDay: unknown;
  };
}

export default class HotelOverviewDashboardTilesTopInsightsRatesChart extends Component<HotelOverviewDashboardTilesTopInsightsRatesChartSignature> {
  // eslint-disable-next-line ember/no-unused-services
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare odAccess: Services['od-access'];
  @service declare odSelections: Services['od-selections'];

  @tracked isPriceOpportunityVisible = true;
  @tracked seriesVisibility = {};
  @tracked tooltipData = null;

  isInitalLoad = true;
  RENTAL_RATE = 'rental-rate';

  // Amount of ticks on the yAxis. Same as RI Rates graph.
  tickAmount = 7;
  UNIX_DIFFERENCE_BETWEEN_INDEXES = 1000 * 60 * 60 * 24;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get activeDrilldownDayPlotLines() {
    // @ts-expect-error: Incorrect type
    if (this.odSelections.isDayDrilldownActive) {
      const activeDrilldownDayXvalue = this.xValue(
        this.hotelAndMarketService.toLocalTime(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          this.odSelections.activeDrilldownDayAsString,
        ),
      );

      return [
        {
          color: 'var(--prism-color-chart-accent-secondary-figure-default)',
          value: activeDrilldownDayXvalue,
          zIndex: 1, // In order to position plotLines above plotBands else theya re hidden beneath the weekendPlotBand
        },
      ];
    }

    return [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get canShowPriceOpportunityData() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.odAccess.isMarketInsightFull && this.hasPriceOpportunityData;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    // @ts-expect-error: Incorrect type
    const chartSeries = [];
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    if (this.isInitalLoad) {
      this.setInitialSeriesVisibility();
    }

    this.isInitalLoad = false;

    // Filter out the hotels that are removed i.e. have no matching with the selected OTA (same behavior as in Rates tab)
    const filteredOwnHotelAndCompetitors =
      this.hotelAndMarketService.ownHotelAndCompetitors.filter(
        (hotelInfo) =>
          // @ts-expect-error: Incorrect type
          hotelInfo.otas.find((ota) => ota.id === this.args.otaId) !==
          undefined,
      );
    const availableHotelInfoIds = filteredOwnHotelAndCompetitors.map(
      (competitor) => competitor.id,
    );

    const shortTermRentalSeries = {
      hotelId: SHORT_TERM_RENTAL,
      competitorIndex: this.RENTAL_RATE,
      name: this.intl.t('short_term_rental.rate'),
      type: 'line',
      color: SHORT_TERM_RENTAL_RATE,
      data: [],
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      visible: that.seriesVisibility[SHORT_TERM_RENTAL],
      lineWidth: 2,
      zoneAxis: 'x',
      marker: {
        enabled: true,
        radius: 4,
      },
    };

    filteredOwnHotelAndCompetitors.forEach((hotelInfo) => {
      const hotelInfoId = hotelInfo.id;
      const isCurrentHotel =
        // @ts-expect-error: Incorrect type
        hotelInfoId === this.hotelAndMarketService.hotel.hotelinfo.id;
      let previousValueX = 0;
      // @ts-expect-error: Incorrect type
      let previousValueY = null;

      let hotelSeriesConfig = getSeriesConfiguration(
        hotelInfo,
        [],
        isCurrentHotel,
        this.hotelAndMarketService.activeCompetitors,
      );

      // Retrieve the index of the hotel in the active competitor list to use it for SECONDARY colors
      const competitorIndex =
        this.hotelAndMarketService.activeCompetitors.findIndex(
          (competitor) => competitor.id === hotelInfoId,
        );

      hotelSeriesConfig = {
        ...hotelSeriesConfig,
        zIndex: isCurrentHotel ? 2 : 1, // Honestly don't know why the other zIndex does not work, seems to be a problem with the arearange (Same problem faced in Rates evolution chart)
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        visible: that.seriesVisibility[hotelInfoId],
        competitorIndex: competitorIndex + 2, // Add 2 to map competitorId to the correct secondary color
      };

      const colorWithoutOpacity = hotelSeriesConfig.color;
      const colorWithOpacity =
        HotelOverviewDashboardTilesTopInsightsRatesChart.addOpacity(
          colorWithoutOpacity,
          0.3,
        );

      const activeDrilldownDayXvalue = this.xValue(
        this.hotelAndMarketService.toLocalTime(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          this.odSelections.activeDrilldownDayAsString,
        ),
      );

      // @ts-expect-error: Incorrect type
      if (this.odSelections.isDayDrilldownActive) {
        // Fading up the series when a day is drilled down
        // Cannot directly set up the opacity property of the series since it causes the entire series to fade down including the markers as well
        hotelSeriesConfig.color = colorWithOpacity;
      }

      // priceDifferenceData does not include data from the past but we want to still display the xValues of those days in the past so that the xAxis is same for both Rates and Performance chart
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (this.args.priceDifferenceData.fromDates.length) {
        const startingDateOfPriceDifferenceData =
          this.hotelAndMarketService.toLocalTime(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            this.args.priceDifferenceData.fromDates[0].fromDate,
          );
        const startingDateOfPriceDifferenceDataXValue = this.xValue(
          startingDateOfPriceDifferenceData,
        );
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const startRangeXValue = this.xValue(this.args.selectedRange.start);

        const differenceInXvalues =
          startingDateOfPriceDifferenceDataXValue - startRangeXValue;

        if (differenceInXvalues) {
          const differenceInDays =
            differenceInXvalues / this.UNIX_DIFFERENCE_BETWEEN_INDEXES - 1;

          for (let count = 0; count < differenceInDays; count++) {
            const nullDataObject = {
              x:
                startRangeXValue + this.UNIX_DIFFERENCE_BETWEEN_INDEXES * count,
              y: null,
            };

            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            hotelSeriesConfig.data.push(nullDataObject);
          }
        }
      }

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.args.priceDifferenceData.fromDates.forEach((datapoint, index) => {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const selectedRate = (datapoint.kpis.rates?.[hotelInfoId] ?? [])[0];
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const { fromDate } = datapoint;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        const period = this.hotelAndMarketService.toLocalTime(fromDate);

        const isSoldOut =
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          !selectedRate || !selectedRate.value || selectedRate.value === 0;

        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        const otbDatapointObject = this.args.otbData?.fromDates?.find(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          (data) => data.fromDate === fromDate,
        );

        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        const rentalOtbDatapointObject = this.args.rentalData?.fromDates?.find(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          (data) => data.fromDate === fromDate,
        );

        // Filter out the removed competitors
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        const ratesArray = Object.values(datapoint.kpis.rates)
          .flat()
          .filter((hotelRateObject) =>
            // @ts-expect-error: Incorrect type
            availableHotelInfoIds.includes(`${hotelRateObject.hotelinfo}`),
          );

        if (
          this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.short_term_rental)
        ) {
          ratesArray.push({
            hotelinfo: this.RENTAL_RATE,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            value: rentalOtbDatapointObject?.getDataValueFromKey(
              this.odSelections,
              OD_API_DATA_KEYS.rental_rate,
            ),
          });
        }

        const xValue = this.xValue(period);
        const xSoldOut = this.xSoldOut(period);

        let activeDrilldownDayMarker = {
          enabled: true,
          fillColor: colorWithoutOpacity,
        };

        let position = null;
        let returnDict;

        if (!isSoldOut) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          position =
            HotelOverviewDashboardTilesTopInsightsRatesChart.calculatePricePosition(
              hotelInfoId,
              ratesArray,
            );

          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          [returnDict, previousValueX, previousValueY] = connectRateDataPoint(
            this.hotelAndMarketService,
            xValue,
            xSoldOut,
            hotelSeriesConfig.zones,
            selectedRate,
            this.yAxisMax,
            hotelInfoId,
          );

          // Fade out the outliers' data labels in case a day is drilled down. The data labels use the default black colour ('#000000')
          if (
            // @ts-expect-error: Incorrect type
            this.odSelections.isDayDrilldownActive &&
            // @ts-expect-error: Incorrect type
            fromDate !== this.odSelections.activeDrilldownDayAsString &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            returnDict.dataLabels
          ) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            returnDict.dataLabels.color =
              HotelOverviewDashboardTilesTopInsightsRatesChart.addOpacity(
                '#000000',
                0.3,
              );
          }
        } else {
          // Persist the look of drilled down day markers for sold out days
          activeDrilldownDayMarker = {
            ...activeDrilldownDayMarker,
            ...HIGHCHARTS_MISSING_MARKER,
          };

          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          [returnDict, previousValueX] = connectUndefinedRateDataPoint(
            previousValueX,
            // @ts-expect-error: Incorrect type
            previousValueY,
            hotelSeriesConfig.zones,
            xValue,
            xSoldOut,
            index,
            selectedRate,
            hotelInfoId,
          );

          // Fade out the sold out (cross) day markers in case a day is drilled down. We use 'grey' lineColor for these markers which has hex code of '#808080'
          // @ts-expect-error: Incorrect type
          if (this.odSelections.isDayDrilldownActive) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            returnDict.marker.lineColor =
              HotelOverviewDashboardTilesTopInsightsRatesChart.addOpacity(
                '#808080',
                0.3,
              );
          }
        }

        // "Find" is used because it's possible to check dates in the past but MI endpoint doesn't allow it
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const priceOpportunityDatapointObject =
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          this.args.priceOpportunityData?.find(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            (data) => data.date === fromDate,
          );

        const tooltipData = {
          hotelName: hotelInfo.name,
          hotelSeriesColor: colorWithoutOpacity,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          myOtbValue: otbDatapointObject?.getDataValueFromKey(
            this.odSelections,
            OD_API_DATA_KEYS.my_otb_percentage,
          ),
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          myOtbRoomnightsValue: otbDatapointObject?.getDataValueFromKey(
            this.odSelections,
            OD_API_DATA_KEYS.my_otb_roomnights,
          ),
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          myOtbPickupPercentageValue: otbDatapointObject?.getDataValueFromKey(
            this.odSelections,
            OD_API_DATA_KEYS.my_otb_percentage_pickup,
            // @ts-expect-error: Incorrect type
            this.args.selectedRefDaysAgo.id,
          ),
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          myOtbPickupRoomnightsValue: otbDatapointObject?.getDataValueFromKey(
            this.odSelections,
            OD_API_DATA_KEYS.my_otb_roomnights_pickup,
            // @ts-expect-error: Incorrect type
            this.args.selectedRefDaysAgo.id,
          ),
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          marketOtbValue: otbDatapointObject?.getDataValueFromKey(
            this.odSelections,
            OD_API_DATA_KEYS.market_otb_percentage,
          ),
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          marketPickupValue: otbDatapointObject?.getDataValueFromKey(
            this.odSelections,
            OD_API_DATA_KEYS.market_otb_percentage_pickup,
            // @ts-expect-error: Incorrect type
            this.args.selectedRefDaysAgo.id,
          ),
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          rentalOtbValue: rentalOtbDatapointObject?.getDataValueFromKey(
            this.odSelections,
            OD_API_DATA_KEYS.rental_otb,
          ),
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          rentalPickupValue: rentalOtbDatapointObject?.getDataValueFromKey(
            this.odSelections,
            OD_API_DATA_KEYS.rental_otb_diff,
            // @ts-expect-error: Incorrect type
            this.args.selectedRefDaysAgo.id,
          ),
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          price: selectedRate.value,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          position,
          numberOfCompetitors: ratesArray.length,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          message: selectedRate.message,
        };

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        returnDict = {
          ...returnDict,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          fromDate,
          tooltipData,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          hasOpportunity: priceOpportunityDatapointObject?.hasOpportunity,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          opportunity: priceOpportunityDatapointObject?.opportunity,
        };

        // Add activeDrilldownDayMarker to the points corresponding to the drilled down day
        if (activeDrilldownDayXvalue === xValue) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          returnDict.marker = activeDrilldownDayMarker;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        hotelSeriesConfig.data.push(returnDict);
      });

      chartSeries.push(hotelSeriesConfig);
    });

    if (this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.short_term_rental)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const shortTermRentalData = JSON.parse(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        JSON.stringify(chartSeries[chartSeries.length - 1].data),
      );

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      shortTermRentalData.forEach((dataPoint) => {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        const rental = this.args.rentalData?.fromDates?.find(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          (data) => data.fromDate === dataPoint.fromDate,
        );

        // Find the rental rate position
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        const rates = this.args.priceDifferenceData.fromDates.find(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          (data) => data.fromDate === dataPoint.fromDate,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        )?.kpis?.rates;
        if (!rates) {
          return;
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        const ratesArray = Object.values(rates)
          .flat()
          .filter((hotelRateObject) =>
            // @ts-expect-error: Incorrect type
            availableHotelInfoIds.includes(`${hotelRateObject.hotelinfo}`),
          );
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        const currentRentalRate = rental?.getDataValueFromKey(
          this.odSelections,
          OD_API_DATA_KEYS.rental_rate,
        );
        ratesArray.push({
          hotelinfo: this.RENTAL_RATE,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          value: currentRentalRate,
        });
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const rentalRatePosition =
          HotelOverviewDashboardTilesTopInsightsRatesChart.calculatePricePosition(
            this.RENTAL_RATE,
            ratesArray,
          );

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        dataPoint.y = currentRentalRate;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        dataPoint.hotelId = 'rental';
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        dataPoint.tooltipValue = currentRentalRate;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        dataPoint.tooltipData.hotelName = this.intl.t('short_term_rental.rate');
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        dataPoint.tooltipData.price = currentRentalRate;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        dataPoint.tooltipData.hotelSeriesColor = SHORT_TERM_RENTAL_RATE;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        dataPoint.tooltipData.position = rentalRatePosition;
      });
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      shortTermRentalSeries.data = shortTermRentalData;
    }

    // Add price opportunity chartSeries data
    const myPriceOpportunitySeriesConfig = {
      id: 'price-opportunity',
      name: [
        this.intl.t('overview_dashboard.market.price_opportunity'),
        // @ts-expect-error: Incorrect type
        this.args.selectedRefDaysAgo.label,
      ].join(' '),
      zIndex: 20,
      type: 'scatter',
      marker: {
        enabled: true,
        lineColor: 'var(--prism-color-chart-accent-primary-figure-emphasis)',
        lineWidth: 3,
        radius: 4,
        fillColor: 'var(--prism-color-elevation-surface)',
      },
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      visible:
        this.hasSelectedDateRangePriceOpportunities &&
        this.isPriceOpportunityVisible, // Hide if there are no price opportunities in the selected date range
      data: [],
    };

    if (this.canShowPriceOpportunityData) {
      // Price opportunities should be shown on the "Own Hotel" rate line.
      // Therefore, we can reuse the data of "Own Hotel" and filter dates that don't have a price opportunity.
      // @ts-expect-error: Incorrect type
      const ownHotelChartSeriesConfigIdx = chartSeries.findIndex(
        ({ hotelId }) =>
          // @ts-expect-error: Incorrect type
          hotelId === this.hotelAndMarketService.hotel.hotelinfo.id,
      );

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      chartSeries[ownHotelChartSeriesConfigIdx].data = chartSeries[
        ownHotelChartSeriesConfigIdx
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      ].data.map((chartSeriesData) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return {
          ...chartSeriesData,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          tooltipData: {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            ...chartSeriesData.tooltipData,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            hasOpportunity: chartSeriesData.hasOpportunity,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            opportunity: chartSeriesData.opportunity,
          },
        };
      });

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      myPriceOpportunitySeriesConfig.data = chartSeries[
        ownHotelChartSeriesConfigIdx
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      ].data.filter(({ hasOpportunity }) => hasOpportunity);

      // Insert "Price Opportunity" series after "Own Hotel" series
      // @ts-expect-error: Incorrect type
      chartSeries.splice(1, 0, myPriceOpportunitySeriesConfig);
    }

    if (this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.short_term_rental)) {
      chartSeries.push(shortTermRentalSeries);
    }

    return chartSeries;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    return {
      chart: {
        type: 'line',
        height: 350,
        spacingLeft: PRISM_SPACING_400,
        spacingBottom: PRISM_SPACING_400,
        spacingTop: that.args.isPerformanceChartVisible
          ? PRISM_SPACING_400
          : PRISM_SPACING_100, // Needs to be set or the top axis labels are cut off
        spacingRight: PRISM_SPACING_400, // Needs to be set or the secondary Y axis labels are cut off a bit
      },
      scrollbar: {
        enabled: false,
        liveRedraw: false,
      },
      xAxis: that.xAxis,
      yAxis: that.yAxis,
      tooltip: {
        hideDelay: 0.2,
        crosshairs: true,
        padding: 0,
        borderWidth: 0,
        shadow: false,
        distance: 20,
        shared: false,
        outside: true,
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const day = this.point;

          // Necessary to get correct data for tooltips, needs to be in separate runloop
          begin();
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          that.tooltipData = {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            ...day.tooltipData,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            fromDateString: day.fromDate,
          };
          end();

          // @ts-expect-error: Incorrect type
          return document.getElementById('dummy-tooltip-for-rates-chart')
            .innerHTML;
        },
        useHTML: true,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        series: {
          connectNulls: true,
          point: {
            events: {
              // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
              click() {
                // day drilldown on point click
                // @ts-expect-error: Incorrect type
                that.args.toggleOrSetSelectedDay(this.fromDate);
              },
            },
          },
          states: {
            inactive: {
              enabled: false,
            },
          },
        },
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasPriceOpportunityData() {
    // @ts-expect-error: Incorrect type
    return Boolean(this.args.priceOpportunityData?.length);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasSelectedDateRangePriceOpportunities() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.args.priceOpportunityData?.some((data) => data.hasOpportunity);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get tickPositions() {
    const tickPositions = calculateTickPositions(
      this.hotelAndMarketService.hotel,
      this.hotelAndMarketService.activeCompetitors,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      this.args.priceDifferenceData.fromDates.map((fromDate) => fromDate.kpis),
      this.yAxisMax,
      null,
      this.tickAmount,
    );

    if (tickPositions) {
      // Adding 5% of max tick position value for some extra space on top of the chart so that the max value points are not cutt off
      const tickPositionsMaxValue = tickPositions[tickPositions.length - 1];
      // @ts-expect-error: Incorrect type
      const paddingTop = tickPositionsMaxValue + 0.05 * tickPositionsMaxValue;
      tickPositions.push(paddingTop);

      return tickPositions;
    }

    return [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get weekendPlotBands() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const startDate = this.args.selectedRange.start;
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const endDate = this.args.selectedRange.end;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return getWeekendPlotBands(this.hotelAndMarketService, startDate, endDate);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get xAxis() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    return {
      type: 'datetime',
      crosshair: true,
      plotLines: that.activeDrilldownDayPlotLines,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      tickPositioner() {
        return xAxistickPositionerForOverviewDashboard(
          // @ts-expect-error: Incorrect type
          this.dataMin,
          // @ts-expect-error: Incorrect type
          this.dataMax,
        );
      },
      labels: {
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          const date = dayjs.utc(this.value).format('ll');
          return date;
        },
      },
      plotBands: this.weekendPlotBands,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get yAxis() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    return {
      max: that.yAxisMax,
      ceiling: that.yAxisMax + 5,
      showFirstLabel: false,
      tickAmount: that.tickAmount, // same as RI Rates graph
      tickPositions: this.tickPositions,
      gridLineColor: 'transparent', // We set plotlines already
      plotLines: this.yAxisPlotLines,
      title: {
        text: '',
      },
      labels: {
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          // @ts-expect-error: Incorrect type
          if (this.value > that.tickPositions[that.tickPositions.length - 2]) {
            return '';
          }

          const label = that.hotelAndMarketService.formatRate(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.value,
            true,
            false,
          );

          return label === null ? label : `${label}`;
        },
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get yAxisMax() {
    return calculateYaxisMax(
      this.hotelAndMarketService.hotel,
      this.hotelAndMarketService.activeCompetitors,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      this.args.priceDifferenceData.fromDates.map((fromDate) => fromDate.kpis),
    );
  }

  /*
    Problem: The extra 5% padding to the yAxis adds an extra tick. It is possible to hide the label of this tick but not the respective grid line
    Therefore, we draw custom plotLines for all the yAxis ticks except the max + 5% one
  */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get yAxisPlotLines() {
    return this.tickPositions.slice(0, -1).map((value) => ({
      color: 'var(--prism-color-border-neutral-default)',
      value,
    }));
  }

  // Standard formula for color mixing: newComponent = floor(oldComponent x alpha + backgroundComponent x (1 - alpha))
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  static addOpacity(color, opacity) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const alpha = Math.round(Math.min(Math.max(opacity || 1, 0), 1) * 255);
    return `${color}${alpha.toString(16)}`;
  }

  // Position of a hotel: All hotels in the compset that are more expensive than the current hotel + 1
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  static calculatePricePosition(hotelInfoId, ratesArray) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const currentHotelRate = ratesArray.find(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (hotelRateObject) => `${hotelRateObject.hotelinfo}` === hotelInfoId,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    ).value;
    // Filter out the hotels that are more expensive than the current hotel
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const hotelsMoreExpensive = ratesArray.filter(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (hotelRateObject) => hotelRateObject.value > currentHotelRate,
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return hotelsMoreExpensive.length + 1;
  }

  setInitialSeriesVisibility(): void {
    const compset = {};
    this.hotelAndMarketService.ownHotelAndCompetitors.forEach((hotel) => {
      // @ts-expect-error: Incorrect type
      compset[hotel.id] = true;
    });

    if (this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.short_term_rental)) {
      // @ts-expect-error: Incorrect type
      compset[SHORT_TERM_RENTAL] = true;
    }

    this.seriesVisibility = compset;
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  xSoldOut(period) {
    return this.hotelAndMarketService.toLocalUnixTime(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      period.unix() + DAY_IN_SECONDS,
    );
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  xValue(period) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    return this.hotelAndMarketService.toLocalUnixTime(period.unix());
  }

  @action
  // @ts-expect-error: Incorrect type
  toggleSeriesVisibility(chartSeries): void {
    // Only hotels (not the price opportunity) have the hotelId property
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const isHotelSeriesClicked = Boolean(chartSeries.hotelId);

    if (isHotelSeriesClicked) {
      // Use object destructuring as "seriesVisibility" is the @tracked property
      this.seriesVisibility = {
        ...this.seriesVisibility,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        [chartSeries.hotelId]: !this.seriesVisibility[chartSeries.hotelId],
      };
    } else if (this.hasSelectedDateRangePriceOpportunities) {
      this.isPriceOpportunityVisible = !this.isPriceOpportunityVisible;
    }
  }

  <template>
    {{! @glint-nocheck }}
    <div class="od-performance-rates-chart-container-wrapper">
      {{#if @isLoading}}
        <PrismLoadingBar />
      {{else}}
        <GeneralEmptyStateOtbBanner
          @bannerPosition="top"
          @otbSelfServiceEntryPoint={{OTB_SS_ENTRY_POINTS.OD_PERFORMANCE_RATES_CHART}}
          @otbSelfServiceEntryProduct={{OTB_SS_VIA_QP_VALUES.OVERVIEW_DASHBOARD}}
        />
        <GeneralChartsOtaChart
          @chartOptions={{this.chartOptions}}
          @model={{this.chartData}}
          @onUpdateChartReference={{@onUpdateChartReference}}
        />
        <div class="chart-legend chart-legend--od-performance-rates mb-400">
          {{#each this.chartData as |chartSeries|}}
            {{! We should show the series, if the series is a hotel (which means chartSeries.hotelId is truthy) }}
            {{! If the series is not a hotel (=> the price opportunity series) we check if we can show the price opportunities }}
            {{#if (or chartSeries.hotelId this.canShowPriceOpportunityData)}}
              <GeneralChartsLegendsLegendItem
                @id={{if
                  chartSeries.hotelId
                  (concat "secondary-" chartSeries.competitorIndex)
                  "price-opportunity"
                }}
                @isHidden={{not chartSeries.visible}}
                @legendItemClicked={{fn
                  this.toggleSeriesVisibility
                  chartSeries
                }}
                @type={{if chartSeries.hotelId "line" "dot"}}
              >
                {{chartSeries.name}}
                {{#if
                  (and
                    (not chartSeries.hotelId)
                    (not this.hasSelectedDateRangePriceOpportunities)
                  )
                }}
                  <PrismTooltip
                    @text={{t
                      "overview_dashboard.no_opportunities"
                      refDaysAgo=@selectedRefDaysAgo.label
                    }}
                  />
                {{/if}}
              </GeneralChartsLegendsLegendItem>
            {{/if}}
          {{/each}}
        </div>
        <div class="hidden" id="dummy-tooltip-for-rates-chart">
          <HotelOverviewDashboardTooltipsTooltipRatesChart
            @selectedRefDaysAgo={{@selectedRefDaysAgo}}
            @tooltipData={{this.tooltipData}}
          />
        </div>
      {{/if}}
    </div>
  </template>
}
